import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ApplicationState } from '../../store';
import { EmptyGuid, KeyValuePair, SelectOption } from '../../models/Common';
import {
    Box, Paragraph, Heading, Text, Button, Anchor, DataTable, Tip, Layer, Form, FormField, SelectMultiple,
    Grid, Table, TableHeader, TableRow, TableCell, TableBody, Header, Footer, TextInput,
    CheckBox, TextArea
} from 'grommet';
import { View, Trash, Edit, Download, StatusCritical, StatusGood, Share, Group, User } from 'grommet-icons';

import { retrieveLocalizedStrings, siteSlice } from '../../store/Site';
import BasePage from '../../components/BasePage';
import {
    retrieveCountries, retrievComplianceReports, retrievComplianceReport, retrieveComplianceOptions, saveComplianceReport,
    deleteComplianceReport, downloadComplianceReport
} from '../../store/Plmt';
import _ from 'lodash';
import { isPLMTAdmin, isInternalPLMTUser, hasRole, getPLMTGeographies, hasWorldWide } from '../../Utilities';
import { ComplianceReport } from '../../models/plmt/ComplianceReport';
import { format, parseISO } from 'date-fns';
import { ComplianceSummary } from '../../models/plmt/ComplianceSummary';

const mapDispatchToProps = {
    retrieveCountries,
    retrievComplianceReports,
    retrievComplianceReport,
    retrieveComplianceOptions,
    retrieveLocalizedStrings,
    saveComplianceReport,
    deleteComplianceReport,
    downloadComplianceReport,
    ...siteSlice.actions,
}

interface IReportsProps {
}

interface IReportsState {
    report?: ComplianceReport;
    addReport: boolean;
    loadingReports: boolean;
    geographies?: string[];
    signingEntities?: SelectOption[];
    programs?: SelectOption[];
    filteredPrograms?: SelectOption[];
    filteredEntities?: SelectOption[];
    isReportValid: boolean;
    optionSelected: boolean;
    allGeographies?: string[];
    allSigningEntities?: SelectOption[];
    allPrograms?: SelectOption[];
}

type ReportsProps = IReportsProps &
    ApplicationState // ... state we've requested from the Redux store
    & typeof mapDispatchToProps
    & RouteComponentProps<any>; // ... plus incoming routing parameters

class Reports extends React.PureComponent<ReportsProps, IReportsState> {
    constructor(props: ReportsProps) {
        super(props);

        let urlLanguage = this.props.match
            ? this.props.match.params.lang
                ? this.props.match.params.lang
                : "en-US"
            : "en-US";
        let langInfo = urlLanguage.split('-');
        let keys = ['ExternalPages.Certification_And_Learning_Label', 'Metadata.PlmtKeywords', 'Metadata.PlmtDescription',
            'MyLearning.Reports_Label', 'MyLearning.PLMT_Title', 'MyLearning.LastModifiedDate_Label', 'MyLearning.ShareReport_Label',
            'MyLearning.PLMT_Regional_Analysis_Reports_Header', 'MyLearning.PLMT_Regional_Gap_Reports_Header',
            'MyLearning.Not_Authorized_Statement', 'MyLearning.PlmtReport_Name_Label', 'MyLearning.PlmtReport_Type_Label',
            'MyLearning.PlmtReport_AvailableReports_Label', 'MyLearning.PlmtReport_Owner_Label', 'MyLearning.Download_Label',
            'MyLearning.PlmtReport_ComplianceReportBuilder_Label', 'MyLearning.PlmtReport_WWReport_Tooltip', 'MyLearning.View_Label',
            'MyLearning.PlmtReport_ComplianceReport_Statement', 'MyLearning.Display_Label', 'MyLearning.Run_Label', 'MyLearning.MakePrivate_Label',
            'MyLearning.Edit_Label', 'MyLearning.Delete_Label', 'MyLearning.CreateReport_Label', 'MyLearning.Cancel_Label',
            'MyLearning.Geographies_Label', 'MyLearning.Programs_Label', 'MyLearning.SigningEntities_label', 'MyLearning.Create_Label',
            'MyLearning.CustomReport_Statement', 'MyLearning.CustomReport_Title', 'ExternalPages.Reset_Label', 'MyLearning.LastProcessed_Label',
            'General.Display_Exam_Description', 'MyLearning.Save_Label', 'ExternalPages.Internal_PBMUserGuide_label',
            'ExternalPages.CustomReport_LastProcessed_Tooltip'];

        this.state = {
            addReport: false,
            loadingReports: false,
            isReportValid: false,
            optionSelected: false
        };
        this.props.retrieveLocalizedStrings(keys, langInfo[0]);
        this.props.retrieveCountries();
        this.props.setActiveMenu('admin-tools-menu');
    }

    public componentDidUpdate(prevProps: Readonly<ReportsProps>, prevState: Readonly<IReportsState>, snapshot?: any): void {
        let site = this.props.site;
        let user = site.user;
        let isInternalUser = isInternalPLMTUser(user);

        if (user && isInternalUser) {
            if (!this.props.plmt.complianceReports && !this.props.site.loading && !this.state.loadingReports) {
                this.props.retrievComplianceReports();
                this.props.retrieveComplianceOptions({}, this.onOptionsCallback);
                this.setState({ loadingReports: true });
            }
            if ((site.actualUser && site.reload) || (!site.actualUser && site.reload)) {
                this.props.retrievComplianceReports();
                this.props.retrieveComplianceOptions({}, this.onOptionsCallback);
                this.props.setReload(false);
            }
        }
    }

    hasGeography = (geography: string) => {
        let user = this.props.site.user;
        let geographies = getPLMTGeographies(user);
        let hasValue = _.some(geographies, geo => {
            return _.includes(geo, geography);
        });

        return hasValue;
    }

    onCreateReport = () => {
        if (this.state.report) {
            this.props.saveComplianceReport(this.state.report, this.reportCallback);
        }
        this.onCancelReport();
    }

    reportCallback = () => {
        this.props.retrievComplianceReports();
    }

    onOptionsSelected = (selected: any) => {
        this.setState({ optionSelected: true });
    }

    updateOptions = (evt: any) => {
        let request = {
            geographies: this.state.report?.geographies,
            programs: _.map(this.state.report?.programs, entry => entry.value),
            signingEntities: _.map(this.state.report?.signingEntities, entry => Number(entry.value)),
        };

        this.validateReport();
        if (this.state.optionSelected) {
            this.props.retrieveComplianceOptions(request, this.onOptionsCallback);
        }
        this.setState({ filteredEntities: undefined, filteredPrograms: undefined, optionSelected: false });
    }

    onOptionsCallback = (data: any) => {
        this.setState({
            geographies: data.geographies,
            programs: data.programs,
            signingEntities: data.signingEntities,
            filteredEntities: undefined,
            filteredPrograms: undefined,
            loadingReports: false
        });
    }

    onSearchPrograms = (keywords: string) => {
        let filtered: SelectOption[] = _.filter(this.state.programs, prg => _.includes(prg.text.toLowerCase(), keywords.toLowerCase()) ||
            _.includes(prg.value.toLowerCase(), keywords.toLowerCase()));

        this.setState({ filteredEntities: undefined, filteredPrograms: filtered });
    }

    onSearchEntities = (keywords: string) => {
        let filtered: SelectOption[] = _.filter(this.state.signingEntities, entity => _.includes(entity.text.toLowerCase(), keywords.toLowerCase()) ||
            _.includes(entity.value.toLowerCase(), keywords.toLowerCase()));

        this.setState({ filteredEntities: filtered, filteredPrograms: undefined });
    }

    onResetReport = () => {
        let rpt = this.state.report ? JSON.parse(JSON.stringify(this.state.report)) : {};

        delete rpt.name;
        delete rpt.geographies;
        delete rpt.description;
        delete rpt.signingEntities;
        delete rpt.programs;

        this.setState({ report: rpt });
        this.props.retrieveComplianceOptions({}, this.onOptionsCallback);
    }

    onValidateReport = (evt: any) => {
        this.validateReport();
    }

    validateReport = () => {
        let report = this.state.report;
        let signingEntitiesValid = report?.signingEntities !== undefined && report?.signingEntities.length !== 0;
        let programsValid = report?.programs !== undefined && report?.programs.length !== 0;
        let isValid = report?.name != undefined && ((signingEntitiesValid || programsValid) ||
            (report?.geographies !== undefined && (report?.geographies.length > 1 || programsValid || signingEntitiesValid)));

        this.setState({ isReportValid: isValid });
    }

    onDeleteReport = (rpt: ComplianceReport) => {
        this.props.deleteComplianceReport(rpt, this.reportCallback);
    }

    onEditReport = (rpt: ComplianceReport) => {
        this.setState({ report: rpt, addReport: true });
    }

    onShareReport = (rpt: ComplianceReport, shared: boolean) => {
        let report = JSON.parse(JSON.stringify(rpt));

        report.isShared = shared;
        this.props.saveComplianceReport(report, this.reportCallback);
    }

    onCancelReport = () => {
        this.setState({
            addReport: false,
            report: undefined,
            filteredEntities: undefined,
            filteredPrograms: undefined
        });
        this.props.retrieveComplianceOptions({}, this.onOptionsCallback);
    }

    public render() {
        let site = this.props.site;
        let user = site.user;
        let langInfo = site.currentLanguage.value.split('-');
        let localUrl = langInfo[0] !== 'en'
            ? `${process.env.REACT_APP_MAIN_SITE}/${langInfo[0]}`
            : `${process.env.REACT_APP_MAIN_SITE}`;
        let documentServer = `${process.env.REACT_APP_MAIN_SITE}`;
        let title = site.localizedStrings['MyLearning.PLMT_Title'];
        let page = site.localizedStrings['MyLearning.Reports_Label'];
        let breadcrumbs: KeyValuePair[] = [
            { key: site.menuStrings['Datacard.Overview_Label'], value: `${localUrl}/` },
            { key: site.menuStrings['Datacard.Resources_Label'], value: "" },
            { key: title, value: `${localUrl}/plmt` },
            { key: page, value: "" }
        ];
        let isInternalPlmtUser = isInternalPLMTUser(user);
        let geographies: any = _.map(_.uniqBy(this.props.plmt.countries, c => c.geography), ctry => ctry.geography);
        let canShare = hasRole(user, ["PLMT_ADMIN_ALL", "PLMT_ADMIN_WORLDWIDE"])
        let columns: any = [
            {
                property: 'name',
                header: <Text weight="bold">{site.localizedStrings['MyLearning.PlmtReport_Name_Label']}</Text>,
                render: datum => <Tip dropProps={{ align: { top: "top", left: "right" } }}
                    content={<Box width={{ min: "small", max: "small" }} height={{ max: "small" }} overflow="auto">
                        <Paragraph>{datum.description}</Paragraph>
                    </Box>}><Text>{datum.name}</Text></Tip>
            },
            {
                property: 'isShared',
                header: <Text weight="bold">{site.localizedStrings['MyLearning.PlmtReport_Type_Label']}</Text>,
                render: datum => <Text>{datum.isShared ? "Shared" : datum.isPublic ? "Public" : "Private"}</Text>
            },
            {
                property: 'owner.text',
                header: <Text weight="bold">{site.localizedStrings['MyLearning.PlmtReport_Owner_Label']}</Text>
            },
            {
                property: 'lastModifiedDate',
                header: <Text weight="bold">{site.localizedStrings['MyLearning.LastModifiedDate_Label']}</Text>,
                render: datum => <Text>{format(parseISO(datum.lastModifiedDate), 'yyyy-MMM-dd HH:mm:ss')}</Text>
            },
            {
                property: 'lastProcessed',
                header: <Text weight="bold">{site.localizedStrings['MyLearning.LastProcessed_Label']}</Text>,
                render: datum => {
                    let date = datum.lastProcessed
                        ? format(parseISO(datum.lastProcessed), 'yyyy-MMM-dd HH:mm:ss')
                        : !datum.isPublic
                            ? "N/A"
                            : "";

                    return date === "N/A"
                        ? <Tip dropProps={{ align: { top: "top", left: "left" } }}
                            content={<Box width="small" height={{ max: "small" }} overflow="auto">
                                <Paragraph>{site.localizedStrings['ExternalPages.CustomReport_LastProcessed_Tooltip']}</Paragraph>
                            </Box>}><Text>{date}</Text></Tip>
                        : <Text>{date}</Text>
                }
            },
            {
                header: "Actions",
                render: datum => {
                    let isOwner = datum.owner?.value == user?.email;

                    return <Box direction="row" gap="xsmall">
                        <Button tip={site.localizedStrings['MyLearning.Download_Label']} icon={<Download size='small' />}
                            onClick={() => { this.props.downloadComplianceReport(datum); }} />
                        {isOwner && <Button tip={site.localizedStrings['MyLearning.Edit_Label']} icon={<Edit size='small' />}
                            onClick={() => { this.onEditReport(datum); }} />}
                        {isOwner && <Button tip={site.localizedStrings['MyLearning.Delete_Label']} icon={<Trash size='small' color='red' />}
                            onClick={() => { this.onDeleteReport(datum); }} />}
                        {isOwner && canShare && datum.isShared && <Button tip={site.localizedStrings['MyLearning.MakePrivate_Label']}
                            icon={<User size='small' />} onClick={() => { this.onShareReport(datum, false); }} />}
                        {isOwner && canShare && !datum.isShared && <Button tip={site.localizedStrings['MyLearning.ShareReport_Label']}
                            icon={<Group size='small' />} onClick={() => { this.onShareReport(datum, true); }} />}
                    </Box>
                }
            }];
        let reports = _.orderBy(this.props.plmt.complianceReports, ["isPublic", "isShared", "name"], ["asc", "desc", "asc"]);
        let rptGeographies: any = _.sortBy(this.state.geographies, geo => geo);
        let rptSigningEntities: any = _.sortBy(this.state.filteredEntities ?? this.state.signingEntities, entry => entry.text);
        let rptPrograms: any = _.sortBy(this.state.filteredPrograms ?? this.state.programs, entry => entry.text);

        if (!user) {
            return null;
        }
        if (user && !isInternalPlmtUser) {
            return <Box align="center" justify="center" pad={{ top: 'large', bottom: "large" }}>
                <Text size='large' textAlign="center" weight="bold">{site.localizedStrings['MyLearning.Not_Authorized_Statement']}</Text>
            </Box>;
        }
        geographies = _.sortBy(_.filter(geographies, geography => this.hasGeography(geography)), geography => geography);
        return <BasePage breadcrumbs={breadcrumbs} title={title} pageName="Reports" {...this.props}
            metadataKeywords={site.localizedStrings['Metadata.PlmtKeywords']}
            metadataDescription={site.localizedStrings['Metadata.PlmtDescription']} >
            {site.stringsLoaded && <Box gap="small" pad={{ bottom: "small" }}>
                <Box gap="xsmall">
                    <Box fill="horizontal">
                        <Heading textAlign="center" fill >{(title ?? "")}</Heading>
                    </Box>
                    <Box alignSelf="center" background="#C140FF" border={{ side: 'all', color: '#C140FF', size: "medium" }} width="small"></Box>
                </Box>
                <Heading level={3}>{page}</Heading>
                <Box width="medium">
                    <Anchor label={`${site.localizedStrings['ExternalPages.Internal_PBMUserGuide_label']}`} target="_blank"
                        href={`${localUrl}/documents/plmt/Partner Learning Management Tool (PLMT) User Guide.pdf`} />
                </Box>
                <Grid pad="small" gap={{ row: "small", column: "small" }} rows={["auto"]} columns={["medium", "auto"]} >
                    <Box gap="small" border={{ side: "all", size: "small" }}>
                        <Heading level={4} textAlign='center' fill>{site.localizedStrings['MyLearning.PLMT_Regional_Analysis_Reports_Header']}</Heading>
                        <Box pad="small" gap="small">
                            <ul>
                                {(hasWorldWide(user) || isPLMTAdmin(user)) &&
                                    <li>
                                        <Tip dropProps={{ align: { top: "top", left: "right" } }}
                                            content={<Box width="small" style={{ zIndex: 1000 }}>
                                                {site.localizedStrings['MyLearning.PlmtReport_WWReport_Tooltip']}
                                            </Box>}>
                                            <Anchor label='World Wide' target="_blank" href={`${documentServer}/documents/plmtReport/plmtActivities_WW.xlsx`} />
                                        </Tip>
                                    </li>
                                }
                                {_.map(geographies, geography => {
                                    let regexp = new RegExp(" ", "g");
                                    let noSpaceGeo = _.replace(geography, regexp, "");

                                    return <li>
                                        <Anchor label={`Geo - ${geography}`} target="_blank"
                                            href={`${documentServer}/documents/plmtReport/plmtActivities_${noSpaceGeo}.xlsx`} />
                                    </li>
                                })}
                            </ul>
                        </Box>
                    </Box>
                    <Box gap="small" border={{ side: "all", size: "small" }}>
                        <Heading level={4} textAlign='center' fill>{site.localizedStrings['MyLearning.PLMT_Regional_Gap_Reports_Header']}</Heading>
                        <Box width="small" pad={{ left: "small", bottom: "small" }}>
                            <Button primary label={site.localizedStrings['MyLearning.CreateReport_Label']}
                                onClick={() => { this.setState({ addReport: true }); }} />
                        </Box>
                        <Box gap="small" height={{ min: "small", max: "medium" }} overflow="auto">
                            <DataTable pin="header" data={reports} primaryKey={false} columns={columns} />
                        </Box>
                    </Box>
                </Grid>
                {this.state.addReport && <Layer>
                    <Box pad="small" gap="small" width={{ min: "medium", max: "large" }}>
                        <Header fill>
                            <Heading level={4} textAlign='center'>{site.localizedStrings['MyLearning.CustomReport_Title']}</Heading>
                        </Header>
                        <Paragraph>{site.localizedStrings['MyLearning.CustomReport_Statement']}</Paragraph>
                        <Form value={this.state.report} onChange={nextValue => this.setState({ report: nextValue })}
                            validate='change' onSubmit={this.onCreateReport} onReset={this.onResetReport} onValidate={this.onValidateReport}>
                            <Box gap="small" pad={{ left: "small", right: "small" }}>
                                <Box gap="small">
                                    <FormField fill name="name" htmlFor="name-input" required
                                        label={site.localizedStrings['MyLearning.PlmtReport_Name_Label']} >
                                        <TextInput name="name" id="name-input" maxLength={212} />
                                    </FormField>
                                    <FormField fill name="description" htmlFor="description-input" required
                                        label={site.localizedStrings['General.Display_Exam_Description']} >
                                        <TextArea name="description" id="description-input" maxLength={255} />
                                    </FormField>
                                    {canShare && <Box>
                                        <CheckBox name="isShared" id="isShared-input" label={site.localizedStrings['MyLearning.ShareReport_Label']} />
                                    </Box>}
                                    <FormField fill name="geographies" htmlFor="geographies-input" validateOn='change'
                                        label={site.localizedStrings['MyLearning.Geographies_Label']} >
                                        <SelectMultiple name="geographies" id="geographies-input" options={rptGeographies}
                                            dropHeight="small" onClose={(evt) => { this.updateOptions(evt) }}
                                            onChange={({ value, option }) => { this.onOptionsSelected(option) }} />
                                    </FormField>
                                    <FormField fill name="signingEntities" htmlFor="signingEntities-input" validateOn='change'
                                        label={site.localizedStrings['MyLearning.SigningEntities_label']} >
                                        <SelectMultiple name="signingEntities" id="signingEntities-input" labelKey="text" valueKey="value"
                                            dropHeight="small" options={rptSigningEntities} onClose={this.updateOptions}
                                            onSearch={(keywords) => { this.onSearchEntities(keywords); }} onChange={this.onOptionsSelected} />
                                    </FormField>
                                    <FormField fill name="programs" htmlFor="programs-input" validateOn='change'
                                        label={site.localizedStrings['MyLearning.Programs_Label']} >
                                        <SelectMultiple name="programs" id="programs-input" labelKey="text" valueKey="value" dropHeight="small"
                                            options={rptPrograms} onClose={this.updateOptions}
                                            onSearch={(keywords) => { this.onSearchPrograms(keywords); }} onChange={this.onOptionsSelected} />
                                    </FormField>
                                </Box>
                                <Footer direction="row-responsive" gap="small" alignSelf="end">
                                    <Button secondary type="reset" label={site.localizedStrings['ExternalPages.Reset_Label']} />
                                    {this.state.report?.id === EmptyGuid && <Button primary type="submit" disabled={!this.state.isReportValid}
                                        label={site.localizedStrings['MyLearning.Create_Label']} />}
                                    {this.state.report?.id !== EmptyGuid && <Button primary type="submit" disabled={!this.state.isReportValid}
                                        label={site.localizedStrings['MyLearning.Save_Label']} />}
                                    <Button secondary color="red" label={site.localizedStrings['MyLearning.Cancel_Label']} onClick={this.onCancelReport} />
                                </Footer>
                            </Box>
                        </Form>
                    </Box>
                </Layer>}
            </Box>}
        </BasePage>
    }
}

export default withRouter(connect(
    (state: ApplicationState) => state, // Selects which state properties are merged into the component's props
    mapDispatchToProps)(Reports as any));
